import { MessageState, MessageV2DTO, ReceiverEnvelopeState, TransactionOriginApp } from 'support/types';

export const LINE_ITEMS_BIG_MESSAGE_THRESHOLD = 1000;

export const isBigMessage = (data: Record<string, unknown> | undefined): boolean => {
  const lineItems = data?.line_items as Array<unknown> | undefined;
  return (lineItems?.length ?? 0) > LINE_ITEMS_BIG_MESSAGE_THRESHOLD;
};

/**
 * Message states categorized by processing phase.
 * These help understand where a message is in its lifecycle.
 */

export const TERMINAL_MESSAGE_STATES = [
  MessageState.decanonicalized,
  MessageState.superseded,
  MessageState.post_processed,
  MessageState.pendingReview,
  MessageState.pendingCollection,
  MessageState.dropped,
] as const;

export const FAILED_MESSAGE_STATES = [
  MessageState.decanonicalizedFailed,
  MessageState.enrichedFailed,
  MessageState.routingFailed,
  MessageState.validation_for_destination_failed,
  MessageState.postProcessingFailed,
] as const;

/**
 * Receiver-envelope states the delivery pipeline moves through on its own. A receiver-side resend
 * puts a delivered envelope back to `denormalized`, so these are the states a page has to keep
 * watching to see the redelivery land.
 *
 * `pending_pickup` and `pending_auto_recovery` are deliberately excluded even though neither is a
 * final answer: the first waits on the receiving system to collect the file and the second is a
 * backend retry loop with its own cadence, so an envelope can sit in either indefinitely and
 * polling would never stop.
 */
const IN_FLIGHT_RECEIVER_ENVELOPE_STATES = [
  ReceiverEnvelopeState.initial,
  ReceiverEnvelopeState.decanonicalized,
  ReceiverEnvelopeState.denormalized,
  ReceiverEnvelopeState.deliveryPending,
] as const;

export const isReceiverDeliveryInFlight = (envelopeState: string | undefined): boolean => {
  if (!envelopeState) {
    return false;
  }
  return (IN_FLIGHT_RECEIVER_ENVELOPE_STATES as ReadonlyArray<string>).includes(envelopeState);
};

const PDF_BEARING_ORIGINS: ReadonlyArray<TransactionOriginApp> = [
  'DOCUMENT_UNDERSTANDING_AGENT',
  'UNATTENDED_OCR',
  'UNATTENDED_STRUCTURED_FILE',
];

export const getOriginalPDF = (
  originApp: TransactionOriginApp | null | undefined,
  message: MessageV2DTO | undefined,
): { url: string; filename: string } | undefined => {
  if (!originApp || !PDF_BEARING_ORIGINS.includes(originApp)) {
    return undefined;
  }
  const attachment = message?.attachments?.[0];
  if (attachment?.mime_type !== 'application/pdf') {
    return undefined;
  }
  return { url: attachment.url, filename: attachment.name };
};

export const isMessageBEValidationComplete = (internalState: string | undefined): boolean => {
  if (!internalState) {
    return false;
  }
  return (
    (TERMINAL_MESSAGE_STATES as ReadonlyArray<string>).includes(internalState) ||
    (FAILED_MESSAGE_STATES as ReadonlyArray<string>).includes(internalState)
  );
};
